import { queries, useActiveIncentives } from '@tovala/browser-apis-combinedapi'
import { Button, CaretDownIcon, CaretUpIcon } from '@tovala/component-library'
import { clsx } from 'clsx'
import { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'

import { events } from 'analytics/events'
import { useScheduledDelivery } from 'hooks/orderHistory'
import { useUserTermStatuses } from 'hooks/terms'
import { track } from 'utils/analytics'
import { DATE_FORMATS, formatDate } from 'utils/dates'

import Collapsible, {
  CollapsibleContent,
  CollapsibleTrigger,
} from 'components/common/Collapsible'
import { useToast } from 'contexts/toast'
import { useUser } from 'contexts/user'
import { useTypeformPopup } from 'hooks/typeform'
import {
  getActivePromoCount,
  getAutoApplyIncentiveCardContent,
  getIncentivesToDisplay,
} from 'utils/incentives'
import ProfileHeading from './AccountHeading'
import CancelMealPlanModal, {
  CancelBody,
  CancelWithIncentivesBody,
} from './CancelMealPlanModal'
import CommitmentBlockDialog from './CommitmentBlockDialog'
import ResumeDeliveries from './ResumeDeliveries'
import TakeABreak from './TakeABreak'

type DialogData =
  | { hasIncentives: boolean; type: 'cancel' }
  | { type: 'break' }
  | { type: 'commitment' }
  | { type: 'resumeDeliveries' }

const DeliverySettings = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const { openToast } = useToast()
  const { user } = useUser()

  const [dialogData, setDialogData] = useState<DialogData | null>(null)

  const isActiveSubscription = user.subscription.status === 'active'
  const isCommitment = user.subscription.isCommitment

  const { data: termStatuses = [] } = useUserTermStatuses({ user })
  const isOnBreak = termStatuses.some((term) => term.isOnBreak)

  const { data: breakStatus } = useQuery({
    ...queries.breaks.status({ userID: user.id }),
    enabled: isOnBreak,
  })

  const { scheduledDelivery } = useScheduledDelivery({ userID: user.id })
  const presentUserTermID =
    termStatuses.length > 0 ? termStatuses[0].termID : null
  const hasRecentProcessedOrder =
    scheduledDelivery !== null &&
    presentUserTermID !== null &&
    scheduledDelivery.termId === presentUserTermID - 1

  const { data: activeIncentives = [] } = useActiveIncentives({
    userID: user.id,
  })
  const incentivesToDisplay = getIncentivesToDisplay({
    activeIncentives,
    isFreeDessertCustomer: user.isFreeDessertCustomer,
  })

  const activePromoCount = getActivePromoCount({
    activeIncentives,
    isFreeDessertCustomer: user.isFreeDessertCustomer,
  })

  const { openTypeformPopup } = useTypeformPopup({
    userID: `${user.id}`,
  })

  const openCancelSurvey = () => {
    openTypeformPopup({
      survey: 'cancel',
      onClose: () => {
        openToast({
          heading: 'Meal Plan Canceled',
          message:
            'Your meal plan has been canceled. You can restart at any time.',
          type: 'success',
        })
      },
    })
  }

  const openBreakSurvey = () => {
    openTypeformPopup({
      survey: 'break',
      onClose: () => {
        openToast({
          heading: 'Meal Deliveries Paused',
          message: 'Your meal deliveries have been paused.',
          type: 'success',
        })
      },
    })
  }

  useEffect(() => {
    if (
      !isActiveSubscription &&
      location.pathname === '/account/delivery-settings'
    ) {
      navigate('/account')
    }
  }, [isActiveSubscription, navigate, location.pathname])

  if (!isActiveSubscription) {
    return null
  }

  return (
    <div className="w-full max-w-account">
      <div className="hidden md:block">
        <ProfileHeading to="/account">Delivery Settings</ProfileHeading>
      </div>
      <div className="rounded-lg bg-grey-2 md:bg-grey-0">
        <Collapsible
          defaultOpen={location.pathname === '/account/delivery-settings'}
        >
          {({ open }) => (
            <>
              <CollapsibleTrigger className="flex w-full items-center justify-between space-x-8 p-6 md:hidden">
                <h2 className="shrink-0 text-k/18_120">Delivery Settings</h2>
                <div className="flex w-px grow items-center justify-end">
                  {!open && (
                    <span className="truncate whitespace-nowrap pr-3 text-k/16_125 md:hidden">
                      {isOnBreak
                        ? 'Resume or cancel plan'
                        : 'Pause or cancel plan'}
                    </span>
                  )}
                  {open ? (
                    <div className="h-6 w-6">
                      <CaretUpIcon />
                    </div>
                  ) : (
                    <div className="h-6 w-6">
                      <CaretDownIcon />
                    </div>
                  )}
                </div>
              </CollapsibleTrigger>

              <CollapsibleContent open={open}>
                {() => (
                  <div className="space-y-6 px-6 pb-6 md:mt-6">
                    {isOnBreak ? (
                      <div className="space-y-4">
                        <div className="space-y-1">
                          <h3 className="text-h/14_120 font-semibold">
                            Deliveries are paused
                          </h3>
                          {breakStatus && (
                            <p className="mb-4 text-body-sm text-grey-9">
                              Meal deliveries will resume the week of{' '}
                              {formatDate(breakStatus.resumeTermStartDate, {
                                format: DATE_FORMATS.MONTH_FULL_DAY_YEAR_FULL,
                              })}
                              .
                            </p>
                          )}
                        </div>
                        <ResumeDeliveries userID={user.id}>
                          {({ onClickResume }) => (
                            <Button
                              buttonStyle="dark"
                              onClick={() => {
                                track(events.SETTINGS_TAPPED_RESUME_DELIVERIES)

                                setDialogData({
                                  type: 'resumeDeliveries',
                                })

                                onClickResume()
                              }}
                              size="medium"
                            >
                              Resume Meal Deliveries
                            </Button>
                          )}
                        </ResumeDeliveries>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        <div className="space-y-1">
                          <h3 className="text-h/14_120 font-semibold">
                            Looking to pause?
                          </h3>
                          <p className="text-body-sm text-grey-9">
                            Pause deliveries for up to 8 weeks. We'll remind you
                            when it's time to pick meals again.
                          </p>
                        </div>
                        <div className={clsx({ 'opacity-50': isCommitment })}>
                          <Button
                            buttonStyle="dark"
                            onClick={() => {
                              if (isCommitment) {
                                setDialogData({ type: 'commitment' })
                                return
                              }

                              setDialogData({
                                type: 'break',
                              })

                              track(events.SETTINGS_TAPPED_PAUSE_DELIVERIES)
                            }}
                            size="medium"
                          >
                            Pause Meal Deliveries
                          </Button>
                        </div>
                      </div>
                    )}

                    <div className="space-y-4">
                      <div className="space-y-1">
                        <h3 className="text-h/14_120 font-semibold">
                          Looking to cancel?
                        </h3>
                        <p className="text-body-sm text-grey-9">
                          {hasRecentProcessedOrder && scheduledDelivery ? (
                            <>
                              Your next order has already processed and will
                              arrive on{' '}
                              <span className="font-semibold">
                                {formatDate(
                                  scheduledDelivery.userTermOrderSummary
                                    .deliveryDate,
                                  {
                                    format:
                                      DATE_FORMATS.DOW_FULL_MONTH_ABBR_DAY_SUFFIX,
                                  },
                                )}
                              </span>
                              .{' '}
                            </>
                          ) : null}
                          Continue to cancel all future orders.
                        </p>
                      </div>

                      <div className={clsx({ 'opacity-50': isCommitment })}>
                        <Button
                          buttonStyle="stroke"
                          onClick={() => {
                            if (isCommitment) {
                              setDialogData({ type: 'commitment' })
                              return
                            }

                            setDialogData({
                              hasIncentives: incentivesToDisplay.length > 0,
                              type: 'cancel',
                            })

                            track(events.SETTINGS_TAPPED_CANCEL_DELIVERIES)
                          }}
                          size="medium"
                        >
                          Cancel Meal Plan
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </CollapsibleContent>
            </>
          )}
        </Collapsible>
      </div>

      {dialogData && (
        <>
          {dialogData.type === 'commitment' ? (
            <CommitmentBlockDialog onClose={() => setDialogData(null)} />
          ) : dialogData.type === 'break' ? (
            <TakeABreak
              nextTermID={presentUserTermID}
              onClickClose={(createBreakSuccess) => {
                setDialogData(null)

                if (createBreakSuccess) {
                  openBreakSurvey()
                }
              }}
              userID={user.id}
            />
          ) : dialogData.type ===
            'resumeDeliveries' ? null : dialogData.type === 'cancel' &&
            dialogData.hasIncentives ? (
            <CancelMealPlanModal
              activePromoCount={activePromoCount}
              onCancelSuccess={() => {
                setDialogData(null)

                openCancelSurvey()
              }}
              onClickPauseDeliveries={
                !isOnBreak
                  ? () => {
                      setDialogData({ type: 'break' })
                    }
                  : undefined
              }
              onCloseModal={() => {
                setDialogData(null)
              }}
            >
              <CancelWithIncentivesBody
                incentives={incentivesToDisplay.map((incentive) =>
                  getAutoApplyIncentiveCardContent(incentive),
                )}
                isOnBreak={isOnBreak}
              />
            </CancelMealPlanModal>
          ) : dialogData.type === 'cancel' ? (
            <CancelMealPlanModal
              activePromoCount={activePromoCount}
              heading={
                isOnBreak
                  ? 'Are you sure you want to cancel?'
                  : 'Would you like to\npause deliveries instead?'
              }
              onCancelSuccess={() => {
                setDialogData(null)

                openCancelSurvey()
              }}
              onClickPauseDeliveries={
                !isOnBreak
                  ? () => {
                      setDialogData({ type: 'break' })
                    }
                  : undefined
              }
              onCloseModal={() => {
                setDialogData(null)
              }}
            >
              <CancelBody user={user} />
            </CancelMealPlanModal>
          ) : null}
        </>
      )}
    </div>
  )
}

export default DeliverySettings
